/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useIntl } from 'react-intl'

import type { EuiSelectableOption } from '@elastic/eui'
import {
  EuiIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPopover,
  EuiSelectable,
  EuiPopoverTitle,
  EuiFilterButton,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import genericMessages from '@modules/project-lib/genericMessages'
import { useListRegionsQuery } from '@modules/cluster-admin-lib/hooks'
import { useQueryStringParams } from '@modules/utils/qs'

import { filterSchema } from './schema'

type Props = {
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
}

const RegionFilter: React.FunctionComponent<Props> = ({ isOpen, onToggle, onClose }) => {
  const {
    setQueryStringItem,
    queryStringItems: { region_id: regionId },
  } = useQueryStringParams(filterSchema.parse)

  const { formatMessage } = useIntl()
  const regionsQuery = useListRegionsQuery()

  const { data: items = [], isLoading } = regionsQuery

  const options: EuiSelectableOption[] = items.map((region) => ({
    key: region.id,
    label: region.name,
    checked: regionId === region.id ? 'on' : undefined,
  }))

  const numFilters = options.filter((option) => option.checked === 'on').length

  const button = (
    <EuiFilterButton
      iconType='arrowDown'
      onClick={onToggle}
      isSelected={isOpen}
      hasActiveFilters={numFilters > 0}
      numActiveFilters={numFilters}
    >
      <FormattedMessage {...genericMessages.region} />
    </EuiFilterButton>
  )

  const renderOption = ({ key }: EuiSelectableOption) => {
    const region = items.find(({ id }) => id === key)

    if (!region) {
      return null // sanity
    }

    const iconType = (
      {
        aws: 'logoAWS',
        azure: 'logoAzure',
        gcp: 'logoGCP',
      } as const
    )[region.csp]

    return (
      <EuiFlexGroup gutterSize='m' justifyContent='flexStart' alignItems='center'>
        <EuiFlexItem grow={false}>
          <EuiIcon type={iconType} size='l' />
        </EuiFlexItem>
        <EuiFlexItem grow={true}>{region.name}</EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  return (
    <EuiPopover
      id='region'
      button={button}
      isOpen={isOpen}
      closePopover={onClose}
      panelPaddingSize='none'
    >
      <EuiSelectable
        aria-label={formatMessage(genericMessages.regions)}
        options={options}
        singleSelection={true}
        onChange={(newOptions) => {
          const newRegion = newOptions.find((x) => x.checked)?.key
          setQueryStringItem('region_id', newRegion)
          onClose() // no need to keep open since it's single-choice
        }}
        isLoading={isLoading}
        renderOption={renderOption}
      >
        {(list, search) => (
          <div style={{ width: '20rem' }}>
            <EuiPopoverTitle paddingSize='s'>{search}</EuiPopoverTitle>
            {list}
          </div>
        )}
      </EuiSelectable>
    </EuiPopover>
  )
}

export default RegionFilter
